import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../../../api/generalApi/ApiError";
import {
  DateInput,
  GenderRadioSelect,
  GeneralButton,
  GeneralInput,
  Modal,
} from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import { usePostData } from "../../../../hooks";
import { useToast } from "../../../../Services/ToastContext";
import { useEffect } from "react";

interface EditPatientProps {
  onClose: () => void;
  isAddModalOpen: boolean;
  patientData?: {
    Id?: number;
    FullName?: string;
    PhoneNumber?: string;
    Birthday?: string;
    Gender?: string;
  };
}

interface FormData {
  Id?: number;
  FullName: string;
  PhoneNumber?: string;
  Birthday?: string;
  Gender?: string;
}

function EditPatient({
  onClose,
  isAddModalOpen,
  patientData,
}: EditPatientProps) {
  const { t } = useTranslation();
  const methods = useForm<FormData>({
    defaultValues: {
      Id: patientData?.Id,
      FullName: patientData?.FullName || "",
      PhoneNumber: patientData?.PhoneNumber || "",
      Birthday: patientData?.Birthday || "",
      Gender: patientData?.Gender || "",
    },
  });

  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = usePostData(
    EndPoints.UpdatePatient + "/" + patientData?.Id,
  );

  useEffect(() => {
    if (patientData) {
      methods.reset({
        Id: patientData.Id,
        FullName: patientData.FullName || "",
        PhoneNumber: patientData.PhoneNumber || "",
        Birthday: patientData.Birthday || "",
        Gender: patientData.Gender || "",
      });
    }
  }, [patientData, methods]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = { 
        FullName: data.FullName,
      };

      const response = await mutateAsync(JSON.stringify(payload));

      if (response.Status) {
        queryClient.refetchQueries({
          queryKey: [EndPoints.GetMyPatient],
          exact: false,
        });
        showToast(t("success"), "success");
        onClose();
      } else {
        showToast(response.ErrorMessage || t("error_occurred"), "error");
      }
    } catch (error) {
      if (error instanceof ApiError) {
        showToast(error.message || t("submission_error"), "error");
      } else {
        showToast(t("submission_error"), "error");
      }
    }
  };

  return (
    <Modal header={t("edit_patient")} isOpen={isAddModalOpen} onClose={onClose}>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <GeneralInput
            name="PhoneNumber"
            label={t("phone_number")}
            placeholder={t("phone_number")}
            readOnly={true}
            numericOnly
            maxLength={15}
          />

          <div className="relative flex flex-col gap-4">
            <GeneralInput
              name="FullName"
              label={t("full_name")}
              placeholder={t("full_name")}
              charactersOnly
              maxLength={25}
              required
            />

            <DateInput
              name="Birthday"
              label={t("birth_date")}
              dateConstraint="past"
              hideErrorMessage
              readOnly={true}
              value={patientData?.Birthday}
            />

            <GenderRadioSelect
              name="Gender"
              aria-describedby="gender-description"
              readOnly={true}
              disabled={true}
            />
          </div>

          <div className="flex justify-end gap-2">
            <GeneralButton
              fontColor="body-text"
              title={t("cancel")}
              color="lite"
              onClick={onClose}
            />
            <GeneralButton
              type="submit"
              color="blue"
              title={t("edit_patient")}
              isLoading={isPending}
            />
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}

export default EditPatient;
